/**
 * src/state/actions/useCommitNow.js
 *
 * "Commit this record now" — the shared terminal-commit helper (STG-B-003).
 *
 * Committing a record from its modal is a terminal user action: the form
 * closes the moment it lands, so there are no further keystrokes for the 1.5s
 * save debounce to coalesce. Waiting it out only buys a window in which the
 * edit exists nowhere but this browser — and these collections still live in
 * the shared per-tenant tenant_state blob (DB-CONC-002), so any other writer
 * in the meantime has already staled our cached version and made this write a
 * guaranteed 409. Recovering from that refusal (reload, per-record merge,
 * retry) needs a live page; a navigation inside the window kills it, and the
 * unload's keepalive write carries the same stale version and is refused too.
 * The edit is then gone with no banner and nothing left to retry it — GH #5021
 * for BC/DR, and the same shape again in #5609 (an incident delete that the
 * user was never told did not land) and #5595 / #5569 / #5605 / #5616.
 *
 * So issue the write on the click. flushPendingSave() is the same versioned
 * write the debounce would have made (DB-CONC-001 intact) and collapses safely
 * when one is already in flight, so a burst of commits still ends up as one
 * write plus one follow-up.
 *
 * Call it ONLY on terminal commits. Several record editors call their save
 * action from an input's onChange (EmployeeDetailModal's JML checklist notes,
 * for one), and flushing there would issue a write per keystroke — which is
 * exactly what the debounce exists to prevent. That is why the action hooks
 * take an explicit `{ commit: true }` from the caller that knows it is
 * terminal, rather than committing on every save.
 */
import { useCallback } from 'react';

export function useCommitNow(flushPendingSave) {
  return useCallback(() => {
    try {
      flushPendingSave?.()?.catch?.(() => {
        /* the write's own retry/banner machinery owns the failure */
      });
    } catch {
      /* never let a commit-now failure take down the caller's save */
    }
  }, [flushPendingSave]);
}
